import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Minus, Plus, Truck, RotateCcw, ShieldCheck, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, EASE } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setActive(0);
    setQty(1);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-16 md:grid-cols-2 md:px-10">
        <div className="aspect-[4/5] animate-pulse rounded-2xl bg-beige" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 animate-pulse rounded bg-beige" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-beige" />
          <div className="h-24 w-full animate-pulse rounded bg-beige" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-center">
        <div>
          <p className="font-serif text-4xl text-ink">Piece not found</p>
          <Link to="/shop" className="mt-6 inline-block eyebrow link-underline">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const wished = has(product.slug);
  const buyNow = () => {
    addItem(product, qty);
    navigate("/checkout");
  };

  return (
    <div className="bg-ivory" data-testid="product-page">
      <div className="mx-auto max-w-[1400px] px-5 py-8 md:px-10 md:py-14">
        <nav className="mb-8 text-xs tracking-wide text-ink/50">
          <Link to="/" className="hover:text-ink">Home</Link> /{" "}
          <Link to="/shop" className="hover:text-ink">Shop</Link> /{" "}
          <span className="text-ink">{product.name}</span>
        </nav>

        <div className="grid gap-10 md:grid-cols-2 md:gap-16">
          {/* Gallery */}
          <div className="flex flex-col-reverse gap-4 md:flex-row">
            {product.images.length > 1 && (
              <div className="flex gap-3 md:flex-col">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-testid={`thumb-${i}`}
                    className={`h-20 w-16 overflow-hidden rounded-lg transition-opacity md:h-24 md:w-20 ${
                      active === i ? "ring-1 ring-ink" : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="relative flex-1 overflow-hidden rounded-2xl bg-beige">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={product.images[active]}
                  alt={product.name}
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="product-main-image"
                />
              </AnimatePresence>
              {product.badge && (
                <span className="absolute left-4 top-4 rounded-full bg-ivory/90 px-3 py-1 text-[0.6rem] uppercase tracking-[0.22em] text-ink backdrop-blur">
                  {product.badge}
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="md:sticky md:top-32 md:self-start">
            <Reveal>
              <p className="eyebrow text-gold">{product.category}</p>
              <h1 className="mt-3 font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-5xl" data-testid="product-name">
                {product.name}
              </h1>
              <div className="mt-4 flex items-center gap-3">
                <Stars rating={product.rating} size={15} />
                <span className="text-sm text-ink/60">
                  {product.rating} · {product.review_count} reviews
                </span>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <span className="font-serif text-3xl text-ink" data-testid="product-price">
                  ${product.price.toFixed(0)}
                </span>
                {product.compare_at_price && (
                  <>
                    <span className="text-lg text-ink/40 line-through">
                      ${product.compare_at_price.toFixed(0)}
                    </span>
                    <span className="rounded-full bg-gold/15 px-3 py-1 text-[0.62rem] uppercase tracking-[0.18em] text-gold">
                      Save ${(product.compare_at_price - product.price).toFixed(0)}
                    </span>
                  </>
                )}
              </div>

              <p className="mt-6 text-base leading-relaxed text-ink/70">{product.description}</p>

              {/* Quantity + actions */}
              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center gap-4 rounded-full border border-sand px-4 py-3">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease" data-testid="qty-dec">
                    <Minus size={15} strokeWidth={1.5} />
                  </button>
                  <span className="w-6 text-center text-sm" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} aria-label="Increase" data-testid="qty-inc">
                    <Plus size={15} strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  onClick={() => toggle(product)}
                  aria-label="Wishlist"
                  data-testid="product-wishlist"
                  className="grid h-[50px] w-[50px] place-items-center rounded-full border border-sand transition-colors hover:border-ink"
                >
                  <Heart size={18} strokeWidth={1.5} className={wished ? "fill-gold text-gold" : ""} />
                </button>
              </div>

              <div className="mt-4 flex flex-col gap-3">
                <button
                  onClick={() => addItem(product, qty)}
                  data-testid="add-to-cart"
                  className="w-full rounded-full bg-ink py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors duration-300 hover:bg-gold"
                >
                  Add to Cart
                </button>
                <button
                  onClick={buyNow}
                  data-testid="buy-now"
                  className="w-full rounded-full border border-ink py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ink transition-colors duration-300 hover:bg-ink hover:text-ivory"
                >
                  Buy It Now
                </button>
              </div>

              {/* Trust badges */}
              <div className="mt-8 grid grid-cols-3 gap-3 border-y border-black/5 py-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <ShieldCheck size={20} strokeWidth={1.3} className="text-gold" />
                  <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ink/60">Anti-Tarnish</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Truck size={20} strokeWidth={1.3} className="text-gold" />
                  <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ink/60">Free Shipping $75+</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <RotateCcw size={20} strokeWidth={1.3} className="text-gold" />
                  <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ink/60">30-Day Returns</span>
                </div>
              </div>

              {/* Accordions */}
              <Accordion type="single" collapsible defaultValue="material" className="mt-4">
                <AccordionItem value="material">
                  <AccordionTrigger className="text-[0.72rem] uppercase tracking-[0.2em]" data-testid="acc-material">
                    Material
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ink/70">
                    {product.material}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="anti">
                  <AccordionTrigger className="text-[0.72rem] uppercase tracking-[0.2em]" data-testid="acc-anti">
                    Anti-Tarnish
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ink/70">
                    {product.anti_tarnish}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="care">
                  <AccordionTrigger className="text-[0.72rem] uppercase tracking-[0.2em]" data-testid="acc-care">
                    Care Instructions
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ink/70">
                    {product.care}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ship">
                  <AccordionTrigger className="text-[0.72rem] uppercase tracking-[0.2em]" data-testid="acc-ship">
                    Shipping & Returns
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ink/70">
                    {product.shipping} {product.returns}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </div>

        {/* Reviews */}
        {product.reviews?.length > 0 && (
          <div className="mt-24 border-t border-black/5 pt-16" data-testid="product-reviews">
            <h2 className="font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">
              Customer Reviews
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {product.reviews.map((r, i) => (
                <Reveal key={i} delay={i * 0.05} className="rounded-2xl border border-black/5 bg-cream p-7">
                  <div className="flex items-center justify-between">
                    <Stars rating={r.rating} />
                    <span className="text-xs text-ink/40">{r.date}</span>
                  </div>
                  {r.title && <p className="mt-4 font-serif text-xl text-ink">{r.title}</p>}
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">“{r.body}”</p>
                  <p className="mt-4 text-[0.62rem] uppercase tracking-[0.2em] text-gold">— {r.name}</p>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {product.related?.length > 0 && (
          <div className="mt-24">
            <h2 className="mb-10 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-4 md:gap-x-6">
              {product.related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
